import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, Book, Zap, HelpCircle, Home, Headphones, Settings, Keyboard } from 'lucide-react';
import { documentationSections } from '../../types/docs';

const sectionIcons: { [key: string]: React.ReactNode } = {
  'getting-started': <Book className="w-5 h-5" />,
  'features': <Zap className="w-5 h-5" />,
  'troubleshooting': <HelpCircle className="w-5 h-5" />,
};

const getBreadcrumbs = (pathname: string) => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length <= 1) return [];

  const breadcrumbs = [];
  let currentPath = '';

  for (const part of parts) {
    currentPath += `/${part}`;
    const title = documentationSections
      .flatMap(section => [section, ...(section.subsections || [])])
      .find(item => item.path === currentPath)?.title || part;
    breadcrumbs.push({ path: currentPath, title });
  }

  return breadcrumbs;
};

export const DocsLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const breadcrumbs = getBreadcrumbs(location.pathname);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const sidebarStyle = "rounded-lg hover:bg-zinc-800/50 hover:text-zinc-200 transition-colors flex gap-2 items-center px-0 py-1"

  return (
    <div className="min-h-screen flex">
      {/* Mobile menu button */}
      <button
        onClick={toggleSidebar}
        className="fixed top-4 left-4 p-2 rounded-lg bg-zinc-800/50 backdrop-blur border border-zinc-700/50 text-zinc-400 hover:text-zinc-200 transition-colors md:hidden z-50"
      >
        {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-zinc-900/95 backdrop-blur transform transition-transform duration-200 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 md:static overflow-y-auto border-r border-zinc-800/50`}
      >
        <div className="p-6">
          <div className="flex flex-col mt-12 md:mt-0 gap-4 mb-6">
            <Link 
              to="/help"
              className={sidebarStyle}
            >
              <HelpCircle className="w-5 h-5" /> <span className="-mt-0.5">Help Center</span>
            </Link>
          </div>

          <nav className="space-y-6">
            {/* Documentation Section */}
            <div>
              <h2 className="text-sm uppercase tracking-wider text-zinc-500 mb-3">Documentation</h2>
              <ul className="space-y-1">
                <li>
                  <Link 
                    to="/docs" 
                    className={sidebarStyle}
                  >
                    <Book className="w-5 h-5" /> Overview
                  </Link>
                </li>
              </ul>
            </div>

            {/* Getting Started Section */}
            <div>
              <h2 className="text-sm uppercase tracking-wider text-zinc-500 mb-3">Getting Started</h2>
              <ul className="space-y-1">
                <li>
                  <Link 
                    to="/docs/getting-started/introduction" 
                    className={sidebarStyle}
                  >
                    <Headphones className="w-5 h-5" /> Introduction
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/docs/getting-started/installation" 
                    className={sidebarStyle}
                  >
                    <Settings className="w-5 h-5" /> Installation
                  </Link>
                </li>
              </ul>
            </div>

            {/* Features Section */}
            <div>
              <h2 className="text-sm uppercase tracking-wider text-zinc-500 mb-3">Features</h2>
              <ul className="space-y-1">
                <li>
                  <Link 
                    to="/docs/features/shortcuts" 
                    className={sidebarStyle}
                  >
                    <Keyboard className="w-5 h-5" /> Keyboard Shortcuts
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/docs/features/haptic-feedback" 
                    className={sidebarStyle}
                  >
                    <Zap className="w-5 h-5" /> Haptic Feedback
                  </Link>
                </li>
              </ul>
            </div>

            {/* Troubleshooting Section */}
            <div>
              <h2 className="text-sm uppercase tracking-wider text-zinc-500 mb-3">Troubleshooting</h2>
              <ul className="space-y-1">
                <li>
                  <Link 
                    to="/docs/troubleshooting" 
                    className={sidebarStyle}
                  >
                    <HelpCircle className="w-5 h-5" /> Common Issues
                  </Link>
                </li>
              </ul>
            </div>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1">
        {/* Breadcrumbs */}
        <div className="bg-zinc-900/50 backdrop-blur-sm border-b border-zinc-800/50">
          <div className="max-w-4xl w-screen mx-auto p-6">
            <div className="flex items-center gap-2 text-sm pl-12 md:pl-0">
             <Link
                to="/help"
                className="text-zinc-400 hover:text-zinc-200 transition-colors flex gap-2 items-center truncate"
              >
                <HelpCircle className="w-4 h-4" /> Help
              </Link>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  <ChevronRight className="w-4 h-4 text-zinc-600" />
                  <Link
                    to={crumb.path}
                    className={`${
                      index === breadcrumbs.length - 1
                        ? 'text-yellow-500 w-fit'
                        : 'text-zinc-400 hover:text-zinc-200 truncate min-w-0'
                    } transition-colors whitespace-nowrap overflow-hidden text-ellipsis`}
                  >
                    {crumb.title}
                  </Link>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <main className="max-w-4xl mx-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 md:hidden"
          onClick={toggleSidebar}
        />
      )}
    </div>
  );
};
